import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Truck, Plus, Edit, Trash2, RefreshCw, ClipboardCheck, 
  Save, ArrowLeft, Settings, CheckCircle, Info, PlusCircle
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from 'sonner';
import api from '@/services/api';
import { useApp } from '@/context/AppContext';

const ETAT_OPTIONS = [
  { value: 'obligatoire', label: 'Obligatoire', color: 'bg-red-100 text-red-800' },
  { value: 'recommande', label: 'Recommandé', color: 'bg-orange-100 text-orange-800' },
  { value: 'optionnel', label: 'Optionnel', color: 'bg-gray-100 text-gray-800' }
];

export default function GestionTypesMateriel() {
  const { currentUser: user } = useApp();
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('types');
  
  // Type dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedType, setSelectedType] = useState(null);
  const [formData, setFormData] = useState({
    code: '',
    libelle: '',
    description: '',
    duree_vgp_mois: 6
  });

  // Checklist
  const [checklistElements, setChecklistElements] = useState([]);
  const [selectedTypeForChecklist, setSelectedTypeForChecklist] = useState(null);
  const [checklistData, setChecklistData] = useState({});
  const [savingChecklist, setSavingChecklist] = useState(false);

  // Element dialog
  const [elementDialogOpen, setElementDialogOpen] = useState(false);
  const [deleteElementDialogOpen, setDeleteElementDialogOpen] = useState(false);
  const [selectedElement, setSelectedElement] = useState(null);
  const [elementFormData, setElementFormData] = useState({
    code: '',
    libelle: '',
    has_value: false,
    value_type: 'number',
    value_unit: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [typesData, elementsData] = await Promise.all([
        api.getTypesMateriel(),
        api.getChecklistElements()
      ]);
      setTypes(typesData || []);
      setChecklistElements(elementsData || []);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const loadTypeChecklist = async (type) => {
    setSelectedTypeForChecklist(type);
    try {
      const data = await api.getTypeMaterielChecklist(type.id);
      const checklistMap = {};
      (data.checklist_items || []).forEach(item => {
        checklistMap[item.code] = {
          etat: item.etat,
          valeur: item.valeur || ''
        };
      });
      setChecklistData(checklistMap);
    } catch (error) {
      console.error('Error loading checklist:', error);
      setChecklistData({});
    }
  };

  // Type handlers
  const handleOpenCreate = () => {
    setSelectedType(null);
    setFormData({ code: '', libelle: '', description: '', duree_vgp_mois: 6 });
    setDialogOpen(true);
  };

  const handleOpenEdit = (type) => {
    setSelectedType(type);
    setFormData({
      code: type.code,
      libelle: type.libelle,
      description: type.description || '',
      duree_vgp_mois: type.duree_vgp_mois || type.duree_ct_mois || 6
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.code || !formData.libelle) {
      toast.error('Veuillez remplir le code et le libellé');
      return;
    }
    try {
      if (selectedType) {
        await api.updateTypeMateriel(selectedType.id, formData);
        toast.success('Type mis à jour');
      } else {
        await api.createTypeMateriel(formData);
        toast.success('Type créé');
      }
      setDialogOpen(false);
      loadData();
    } catch (error) {
      console.error('Error saving:', error);
      toast.error('Erreur lors de l\'enregistrement');
    }
  };

  const handleDelete = async () => {
    if (!selectedType) return;
    try {
      await api.deleteTypeMateriel(selectedType.id);
      toast.success('Type supprimé');
      setDeleteDialogOpen(false);
      setSelectedType(null);
      loadData();
    } catch (error) {
      console.error('Error deleting:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  // Checklist handlers
  const handleChecklistEtatChange = (code, etat) => {
    setChecklistData(prev => ({
      ...prev,
      [code]: { ...prev[code], etat }
    }));
  };

  const handleChecklistValeurChange = (code, valeur, field = null) => {
    setChecklistData(prev => {
      if (field === 'interieur') {
        return { ...prev, [code]: { ...prev[code], valeur_interieur: valeur } };
      } else if (field === 'exterieur') {
        return { ...prev, [code]: { ...prev[code], valeur_exterieur: valeur } };
      } else {
        return { ...prev, [code]: { ...prev[code], valeur: valeur } };
      }
    });
  };

  const handleRemoveFromChecklist = (code) => {
    setChecklistData(prev => {
      const newData = { ...prev };
      delete newData[code];
      return newData;
    });
  };

  const handleSaveChecklist = async () => {
    if (!selectedTypeForChecklist) {
      toast.error('Veuillez sélectionner un type de matériel');
      return;
    }
    
    if (!user) {
      toast.error('Vous devez être connecté pour sauvegarder');
      return;
    }
    
    try {
      setSavingChecklist(true);
      const items = Object.entries(checklistData)
        .filter(([_, data]) => data.etat)
        .map(([code, data]) => ({
          code,
          etat: data.etat,
          valeur: data.valeur || null,
          valeur_interieur: data.valeur_interieur || null,
          valeur_exterieur: data.valeur_exterieur || null
        }));
      
      const userName = `${user.prenom || ''} ${user.nom || ''}`.trim() || user.email || 'Utilisateur';
      
      await api.saveTypeMaterielChecklist(
        selectedTypeForChecklist.id,
        { type_materiel_id: selectedTypeForChecklist.id, checklist_items: items },
        user.id,
        userName
      );
      
      toast.success('Checklist sauvegardée avec succès');
    } catch (error) {
      console.error('Error saving checklist:', error);
      toast.error(error.message || 'Erreur lors de la sauvegarde');
    } finally {
      setSavingChecklist(false);
    }
  };

  // Element handlers
  const handleOpenCreateElement = () => {
    setSelectedElement(null);
    setElementFormData({
      code: '',
      libelle: '',
      has_value: false,
      value_type: 'number',
      value_unit: ''
    });
    setElementDialogOpen(true);
  };

  const handleOpenEditElement = (element) => {
    setSelectedElement(element);
    setElementFormData({
      code: element.code,
      libelle: element.libelle,
      has_value: element.has_value || false,
      value_type: element.value_type || 'number',
      value_unit: element.value_unit || ''
    });
    setElementDialogOpen(true);
  };

  const handleSubmitElement = async (e) => {
    e.preventDefault();
    if (!elementFormData.code || !elementFormData.libelle) {
      toast.error('Veuillez remplir le code et le libellé');
      return;
    }
    try {
      if (selectedElement && selectedElement.custom) {
        await api.updateChecklistElement(selectedElement.code, elementFormData, user.id, `${user.prenom} ${user.nom}`);
        toast.success('Critère mis à jour');
      } else {
        await api.createChecklistElement(elementFormData, user.id, `${user.prenom} ${user.nom}`);
        toast.success('Critère créé');
      }
      setElementDialogOpen(false);
      loadData();
    } catch (error) {
      console.error('Error saving element:', error);
      toast.error(error.message || 'Erreur lors de l\'enregistrement');
    }
  };

  const handleDeleteElement = async () => {
    if (!selectedElement) return;
    try {
      await api.deleteChecklistElement(selectedElement.code);
      toast.success('Critère supprimé');
      setDeleteElementDialogOpen(false);
      setSelectedElement(null);
      loadData();
    } catch (error) {
      console.error('Error deleting element:', error);
      toast.error(error.message || 'Erreur lors de la suppression');
    }
  };

  const countChecklistItems = () => {
    return Object.values(checklistData).filter(d => d.etat).length;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link to="/materiel">
          <Button variant="ghost">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour
          </Button>
        </Link>
      </div>

      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-foreground flex items-center gap-2">
            <Settings className="h-8 w-8 text-primary" />
            Configuration Matériel
          </h1>
          <p className="text-muted-foreground mt-1">
            Gérer les types de matériel, les critères et leurs checklists de sécurité
          </p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="types" className="gap-2">
            <Truck className="w-4 h-4" />
            Types de matériel
          </TabsTrigger>
          <TabsTrigger value="checklist" className="gap-2">
            <ClipboardCheck className="w-4 h-4" />
            Checklists sécurité
          </TabsTrigger>
          <TabsTrigger value="criteres" className="gap-2">
            <PlusCircle className="w-4 h-4" />
            Critères ({checklistElements.length})
          </TabsTrigger>
        </TabsList>

        {/* Types Tab */}
        <TabsContent value="types" className="space-y-4">
          <div className="flex justify-end">
            <Button onClick={handleOpenCreate}>
              <Plus className="h-4 w-4 mr-2" />
              Nouveau type
            </Button>
          </div>

          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="pt-6">
              <p className="text-sm text-blue-800">
                <strong>💡 Durée VGP :</strong> Définit la durée de validité de la Vérification Générale Périodique pour chaque type de matériel. 
                La date de fin VGP sera automatiquement calculée lors de l'enregistrement de la dernière vérification.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Code</TableHead>
                    <TableHead>Libellé</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Durée VGP</TableHead>
                    <TableHead>Checklist</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {types.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                        Aucun type de matériel défini
                      </TableCell>
                    </TableRow>
                  ) : (
                    types.map((type) => (
                      <TableRow key={type.id}>
                        <TableCell>
                          <Badge variant="outline">{type.code}</Badge>
                        </TableCell>
                        <TableCell className="font-medium">{type.libelle}</TableCell>
                        <TableCell className="text-muted-foreground text-sm max-w-xs truncate">
                          {type.description || '-'}
                        </TableCell>
                        <TableCell>
                          <Badge className="bg-blue-100 text-blue-800">
                            {type.duree_vgp_mois || type.duree_ct_mois || 6} mois
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => {
                              loadTypeChecklist(type);
                              setActiveTab('checklist');
                            }}
                          >
                            <ClipboardCheck className="w-4 h-4 mr-1" />
                            Configurer
                          </Button>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button variant="ghost" size="icon" onClick={() => handleOpenEdit(type)}>
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => {
                                setSelectedType(type);
                                setDeleteDialogOpen(true);
                              }}
                            >
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Checklist Tab */}
        <TabsContent value="checklist" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Sélectionner un type de matériel</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {types.map(type => (
                  <Button
                    key={type.id}
                    variant={selectedTypeForChecklist?.id === type.id ? 'default' : 'outline'}
                    onClick={() => loadTypeChecklist(type)}
                  >
                    {type.libelle}
                  </Button>
                ))}
              </div>
            </CardContent>
          </Card>

          {selectedTypeForChecklist && (
            <>
              <Card className="bg-green-50 border-green-200">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-green-800">
                        Configuration de la checklist pour: <strong>{selectedTypeForChecklist.libelle}</strong>
                      </p>
                      <p className="text-sm text-green-700 mt-1">
                        {countChecklistItems()} élément(s) configuré(s) sur {checklistElements.length}
                      </p>
                    </div>
                    <Button onClick={handleSaveChecklist} disabled={savingChecklist}>
                      <Save className="w-4 h-4 mr-2" />
                      {savingChecklist ? 'Sauvegarde...' : 'Sauvegarder'}
                    </Button>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Éléments de sécurité</CardTitle>
                  <CardDescription>
                    Définissez l'état requis pour chaque élément: Obligatoire, Recommandé ou Optionnel
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {checklistElements.map((element, index) => (
                      <div 
                        key={element.code} 
                        className={`p-4 rounded-lg border ${
                          checklistData[element.code]?.etat ? 'bg-blue-50 border-blue-200' : 'bg-gray-50 border-gray-200'
                        }`}
                      >
                        <div className="flex flex-col md:flex-row md:items-center gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-sm bg-gray-200 px-2 py-0.5 rounded">
                                {index + 1}
                              </span>
                              <span className="font-medium">{element.libelle}</span>
                              {element.has_value && (
                                <Badge variant="outline" className="text-xs">
                                  <Info className="w-3 h-3 mr-1" />
                                  Avec valeur
                                </Badge>
                              )}
                              {element.custom && (
                                <Badge className="bg-purple-100 text-purple-800 text-xs">
                                  Personnalisé
                                </Badge>
                              )}
                            </div>
                          </div>

                          <div className="flex items-center gap-3">
                            {element.has_value && checklistData[element.code]?.etat && (
                              element.value_type === 'dual_speed' ? (
                                <div className="flex items-center gap-4 p-2 bg-white rounded border">
                                  <div className="flex items-center gap-1">
                                    <span className="text-xs">🏢</span>
                                    <Input
                                      type="number"
                                      value={checklistData[element.code]?.valeur_interieur || ''}
                                      onChange={(e) => handleChecklistValeurChange(element.code, e.target.value, 'interieur')}
                                      className="w-14 h-7 text-sm"
                                      placeholder="5"
                                    />
                                  </div>
                                  <div className="flex items-center gap-1">
                                    <span className="text-xs">🌳</span>
                                    <Input
                                      type="number"
                                      value={checklistData[element.code]?.valeur_exterieur || ''}
                                      onChange={(e) => handleChecklistValeurChange(element.code, e.target.value, 'exterieur')}
                                      className="w-14 h-7 text-sm"
                                      placeholder="10"
                                    />
                                  </div>
                                  <span className="text-xs text-gray-500">{element.value_unit}</span>
                                </div>
                              ) : (
                                <div className="flex items-center gap-2">
                                  <Label className="text-sm whitespace-nowrap">Valeur:</Label>
                                  <Input
                                    type={element.value_type === 'number' ? 'number' : 'text'}
                                    value={checklistData[element.code]?.valeur || ''}
                                    onChange={(e) => handleChecklistValeurChange(element.code, e.target.value)}
                                    className="w-20 h-8"
                                    placeholder="Ex: 10"
                                  />
                                  <span className="text-sm text-gray-500">{element.value_unit}</span>
                                </div>
                              )
                            )}

                            <Select
                              value={checklistData[element.code]?.etat || 'none'}
                              onValueChange={(value) => {
                                if (value === 'none') {
                                  handleRemoveFromChecklist(element.code);
                                } else {
                                  handleChecklistEtatChange(element.code, value);
                                }
                              }}
                            >
                              <SelectTrigger className="w-40">
                                <SelectValue placeholder="Non requis" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">Non requis</SelectItem>
                                {ETAT_OPTIONS.map(opt => (
                                  <SelectItem key={opt.value} value={opt.value}>
                                    {opt.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>

                            {checklistData[element.code]?.etat && (
                              <CheckCircle className="w-5 h-5 text-green-500" />
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Summary */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Résumé de la checklist</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="text-center p-4 bg-red-50 rounded-lg">
                      <p className="text-2xl font-bold text-red-600">
                        {Object.values(checklistData).filter(d => d.etat === 'obligatoire').length}
                      </p>
                      <p className="text-sm text-gray-600">Obligatoires</p>
                    </div>
                    <div className="text-center p-4 bg-orange-50 rounded-lg">
                      <p className="text-2xl font-bold text-orange-600">
                        {Object.values(checklistData).filter(d => d.etat === 'recommande').length}
                      </p>
                      <p className="text-sm text-gray-600">Recommandés</p>
                    </div>
                    <div className="text-center p-4 bg-gray-50 rounded-lg">
                      <p className="text-2xl font-bold text-gray-600">
                        {Object.values(checklistData).filter(d => d.etat === 'optionnel').length}
                      </p>
                      <p className="text-sm text-gray-600">Optionnels</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </>
          )}

          {!selectedTypeForChecklist && (
            <Card>
              <CardContent className="py-12 text-center">
                <ClipboardCheck className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                <p className="text-gray-500">
                  Sélectionnez un type de matériel pour configurer sa checklist de sécurité
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Critères Tab */}
        <TabsContent value="criteres" className="space-y-4">
          <div className="flex justify-end">
            <Button onClick={handleOpenCreateElement}>
              <Plus className="h-4 w-4 mr-2" />
              Nouveau critère
            </Button>
          </div>

          <Card className="bg-amber-50 border-amber-200">
            <CardContent className="pt-6">
              <p className="text-sm text-amber-800">
                <strong>💡 Critères personnalisés :</strong> Vous pouvez ajouter des critères spécifiques à votre activité. 
                Les critères par défaut (19 éléments) ne peuvent pas être supprimés mais peuvent être ignorés dans les checklists.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Code</TableHead>
                    <TableHead>Libellé</TableHead>
                    <TableHead>Avec valeur</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {checklistElements.map((element) => (
                    <TableRow key={element.code}>
                      <TableCell>
                        <Badge variant="outline">{element.code}</Badge>
                      </TableCell>
                      <TableCell className="font-medium">
                        {element.libelle}
                        {element.custom && (
                          <Badge className="ml-2 bg-purple-100 text-purple-800 text-xs">
                            Personnalisé
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {element.has_value ? (
                          <Badge className="bg-blue-100 text-blue-800">
                            Oui ({element.value_unit || element.value_type})
                          </Badge>
                        ) : (
                          <span className="text-gray-400">Non</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {element.custom ? (
                          <Badge className="bg-purple-100 text-purple-800">Personnalisé</Badge>
                        ) : (
                          <Badge variant="outline">Par défaut</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        {element.custom ? (
                          <div className="flex justify-end gap-2">
                            <Button variant="ghost" size="icon" onClick={() => handleOpenEditElement(element)}>
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => {
                                setSelectedElement(element);
                                setDeleteElementDialogOpen(true);
                              }}
                            >
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400">Non modifiable</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Type Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {selectedType ? 'Modifier le type' : 'Nouveau type de matériel'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Code *</Label>
                  <Input
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                    placeholder="CHARIOT"
                    required
                    disabled={!!selectedType}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Durée VGP (mois) *</Label>
                  <Input
                    type="number"
                    min={1}
                    max={24}
                    value={formData.duree_vgp_mois}
                    onChange={(e) => setFormData({ ...formData, duree_vgp_mois: parseInt(e.target.value) || 6 })}
                    required
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Libellé *</Label>
                <Input
                  value={formData.libelle}
                  onChange={(e) => setFormData({ ...formData, libelle: e.target.value })}
                  placeholder="Chariot élévateur"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Description du type de matériel..."
                  rows={3}
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Annuler
              </Button>
              <Button type="submit">
                {selectedType ? 'Mettre à jour' : 'Créer'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Type Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmer la suppression</AlertDialogTitle>
            <AlertDialogDescription>
              Êtes-vous sûr de vouloir supprimer le type "{selectedType?.libelle}" ?
              Cette action est irréversible et supprimera également la checklist associée.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Element Dialog */}
      <Dialog open={elementDialogOpen} onOpenChange={setElementDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {selectedElement?.custom ? 'Modifier le critère' : 'Nouveau critère de checklist'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmitElement}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Code *</Label>
                <Input
                  value={elementFormData.code}
                  onChange={(e) => setElementFormData({ ...elementFormData, code: e.target.value.toUpperCase().replace(/\s+/g, '_') })}
                  placeholder="MON_CRITERE"
                  required
                  disabled={selectedElement?.custom}
                />
                <p className="text-xs text-gray-500">Code unique, en majuscules, sans espaces</p>
              </div>
              <div className="space-y-2">
                <Label>Libellé *</Label>
                <Textarea
                  value={elementFormData.libelle}
                  onChange={(e) => setElementFormData({ ...elementFormData, libelle: e.target.value })}
                  placeholder="Description du critère à vérifier..."
                  rows={2}
                  required
                />
              </div>
              <div className="flex items-center space-x-2">
                <Switch
                  id="has_value"
                  checked={elementFormData.has_value}
                  onCheckedChange={(checked) => setElementFormData({ ...elementFormData, has_value: checked })}
                />
                <Label htmlFor="has_value">Ce critère nécessite une valeur</Label>
              </div>
              {elementFormData.has_value && (
                <div className="grid grid-cols-2 gap-4 pl-6">
                  <div className="space-y-2">
                    <Label>Type de valeur</Label>
                    <Select
                      value={elementFormData.value_type}
                      onValueChange={(value) => setElementFormData({ ...elementFormData, value_type: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="number">Nombre</SelectItem>
                        <SelectItem value="text">Texte</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Unité</Label>
                    <Input
                      value={elementFormData.value_unit}
                      onChange={(e) => setElementFormData({ ...elementFormData, value_unit: e.target.value })}
                      placeholder="km/h, mm, etc."
                    />
                  </div>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setElementDialogOpen(false)}>
                Annuler
              </Button>
              <Button type="submit">
                {selectedElement?.custom ? 'Mettre à jour' : 'Créer'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Element Dialog */}
      <AlertDialog open={deleteElementDialogOpen} onOpenChange={setDeleteElementDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmer la suppression</AlertDialogTitle>
            <AlertDialogDescription>
              Êtes-vous sûr de vouloir supprimer le critère "{selectedElement?.libelle}" ?
              Ce critère sera retiré de toutes les checklists qui l'utilisent.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteElement} className="bg-red-600 hover:bg-red-700">
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
